import { useMutation } from '@apollo/client/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { toaster } from '#components/ui/toaster';
import type { GetDocumentByPathQuery } from '#graphql/generated';
import { UPSERT_DOCUMENT } from '#graphql/mutations/upsert-document';
import { useAppSettings } from '#providers/app-settings-provider';
import { documentViewHref } from '#utils/document-app-path';
import { formatDocumentName } from '#utils/format-document-name';

import {
  type EditorJsData,
  editorJsStableStringForDirtyCompare,
  editorJsStableStringForNewDocPlaceholderCompare,
  normalizeLeadingH1HeaderToDocumentHeading,
  parseEditorJs,
  stringifyEditorJs,
} from './editorjs-types';
import { refetchInTransition } from './refetch-utils';
import { useEditDocument } from './view/edit-document-context';
import { normalizeDocumentLinksInEditorJsData } from './view/viewer/editor-document-links';
import {
  convertEditorJsToMarkdown,
  type ConvertEditorJsToMarkdownError,
} from './view/viewer/editorjs-to-markdown';
import {
  gridV1StableStringForDirtyCompare,
  parseGridV1Document,
  serializeGridV1Document,
} from './view/viewer/grid-v1-serialize';
import {
  createDefaultGridV1DocumentValue,
  type GridV1DocumentValue,
} from './view/viewer/grid-v1-types';
import { createDefaultMarkdownDocumentValue } from './view/viewer/markdown-document-title';
import { createGridV1DocumentFromMarkdown } from './view/viewer/markdown-to-grid-v1';

export type DocumentByPathResult = GetDocumentByPathQuery['documentByPath'];

function getPathFromResult(
  result: DocumentByPathResult,
  fallback: string,
): string {
  return result?.__typename === 'Document'
    ? result.path
    : result?.__typename === 'DocumentNotFound'
      ? (result.requestedPath ?? fallback)
      : fallback;
}

export interface DocumentContextValue {
  document: DocumentByPathResult;
  path: string;
  displayPath: string;
  documentId: string | undefined;
  documentFormat: string | null;
  formatMetadata: unknown;
  canConvertToMarkdown: boolean;
  canConvertToGrid: boolean;
  isMarkdownDocument: boolean;
  isGridDocument: boolean;
  isNewDocument: boolean;
  /** Muted app header / editor until user edits a new-path placeholder. */
  editorPlaceholderMuted: boolean;
  childrenItems: Array<{ id: string; path: string }>;
  childrenHasMore: boolean;
  editorValue: EditorJsData | null;
  markdownValue: string;
  setMarkdownValue: (value: string) => void;
  gridDocumentValue: GridV1DocumentValue;
  setGridDocumentValue: (value: GridV1DocumentValue) => void;
  lastSavedSerialized: string;
  isDirty: boolean;
  dirtyEditGeneration: number;
  getSerializedDocument: () => string;
  convertToMarkdown: () => Promise<void>;
  convertToGrid: () => Promise<void>;
  save: (options?: { silent?: boolean }) => Promise<void>;
  saving: boolean;
  saveInFlightRef: RefObject<boolean>;
  revertToSaved: () => void;
  refetch: () => Promise<unknown>;
}

const DocumentContext = createContext<DocumentContextValue | undefined>(
  undefined,
);

export interface DocumentProviderProps {
  document: DocumentByPathResult;
  path: string;
  refetch: () => Promise<unknown>;
  /** Latest server edit epoch ms from this tab's saves; document page subscription drops stale events against this. */
  lastServerEditTimestampRef: RefObject<number>;
  children: ReactNode;
}

export function DocumentProvider({
  document,
  path,
  refetch,
  lastServerEditTimestampRef,
  children,
}: DocumentProviderProps) {
  const navigate = useNavigate();
  const { documentPathRegex } = useAppSettings();
  const { getValue, setValue, flushSave } = useEditDocument();

  const isNewDocument = document?.__typename === 'DocumentNotFound';
  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath = getPathFromResult(document, path);

  const childrenItems = useMemo(
    () =>
      (document?.children?.items as Array<{
        id: string;
        path: string;
      }> | null) ?? [],
    [document?.children?.items],
  );
  const childrenHasMore = useMemo(
    () => document?.children?.pagination?.hasMore ?? false,
    [document?.children?.pagination?.hasMore],
  );

  const documentValue =
    document?.__typename === 'Document' ? document.value : null;
  const documentFormatFromServer =
    document?.__typename === 'Document'
      ? (document.format ?? 'markdown-v1')
      : 'markdown-v1';
  const createInitialEditorValue = useCallback((): EditorJsData | null => {
    if (
      documentFormatFromServer === 'markdown-v1' ||
      documentFormatFromServer === 'grid-v1'
    ) {
      return null;
    }

    const parsedRaw = documentValue
      ? (parseEditorJs(documentValue) ?? null)
      : null;
    const parsed = parsedRaw?.blocks?.length
      ? normalizeLeadingH1HeaderToDocumentHeading(parsedRaw)
      : parsedRaw;
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    const base: EditorJsData = parsed?.blocks?.length
      ? parsed
      : (() => {
          const segments = formatDocumentName(displayPath);
          const title =
            segments.length > 0
              ? segments[segments.length - 1]
              : displayPath || 'Untitled';
          return {
            time: Date.now(),
            blocks: [{ type: 'documentHeading', data: { text: title } }],
            version: '2.0.0',
          };
        })();
    return normalizeDocumentLinksInEditorJsData(
      base,
      documentPathRegex,
      origin,
    );
  }, [displayPath, documentFormatFromServer, documentPathRegex, documentValue]);
  const initialFormatMetadata =
    document?.__typename === 'Document'
      ? (document.formatMetadata ?? null)
      : null;
  const [documentFormat, setDocumentFormat] = useState<string | null>(
    documentFormatFromServer,
  );
  const [savedDocumentFormat, setSavedDocumentFormat] = useState<string | null>(
    documentFormatFromServer,
  );
  const isMarkdownDocument = documentFormat === 'markdown-v1';
  const isGridDocument = documentFormat === 'grid-v1';
  const [formatMetadata, setFormatMetadata] = useState<unknown>(
    initialFormatMetadata,
  );
  const [savedFormatMetadata, setSavedFormatMetadata] = useState<unknown>(
    initialFormatMetadata,
  );
  const [editorValue, setEditorValue] = useState<EditorJsData | null>(() =>
    createInitialEditorValue(),
  );

  const newDocPlaceholderBaselineRef = useRef(
    editorJsStableStringForNewDocPlaceholderCompare(editorValue),
  );
  const [editorPlaceholderMuted, setEditorPlaceholderMuted] =
    useState(isNewDocument);
  const initialMarkdownValue =
    documentValue ??
    (isNewDocument ? createDefaultMarkdownDocumentValue() : '');
  const [markdownValue, setMarkdownValue] = useState(initialMarkdownValue);
  const [gridDocumentValue, setGridDocumentValue] = useState(() =>
    documentFormatFromServer === 'grid-v1'
      ? parseGridV1Document(documentValue)
      : createDefaultGridV1DocumentValue(),
  );

  const [lastSavedSerialized, setLastSavedSerialized] = useState(() => {
    if (documentFormatFromServer === 'grid-v1') {
      return serializeGridV1Document(parseGridV1Document(documentValue));
    }

    if (documentFormatFromServer === 'markdown-v1') {
      return initialMarkdownValue;
    }

    return stringifyEditorJs(createInitialEditorValue());
  });
  const [dirtyEditGeneration, setDirtyEditGeneration] = useState(0);
  const prevSerializedForAutoSaveRef = useRef(
    documentFormatFromServer === 'grid-v1'
      ? serializeGridV1Document(parseGridV1Document(documentValue))
      : documentFormatFromServer === 'markdown-v1'
        ? initialMarkdownValue
        : editorJsStableStringForDirtyCompare(editorValue),
  );

  const [isDirty, setIsDirty] = useState(false);

  const [upsertDocument, { loading: saving }] = useMutation(UPSERT_DOCUMENT);
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    if (documentFormatFromServer === 'grid-v1') {
      setLastSavedSerialized(
        serializeGridV1Document(parseGridV1Document(documentValue)),
      );
      return;
    }

    setLastSavedSerialized(
      documentFormatFromServer === 'markdown-v1'
        ? (documentValue ??
            (isNewDocument ? createDefaultMarkdownDocumentValue() : ''))
        : stringifyEditorJs(createInitialEditorValue()),
    );
  }, [
    createInitialEditorValue,
    documentFormatFromServer,
    documentValue,
    isNewDocument,
  ]);

  useEffect(() => {
    setDocumentFormat(documentFormatFromServer);
    setSavedDocumentFormat(documentFormatFromServer);
    setFormatMetadata(initialFormatMetadata);
    setSavedFormatMetadata(initialFormatMetadata);
    setEditorValue(createInitialEditorValue());
    if (documentFormatFromServer === 'grid-v1') {
      setGridDocumentValue(parseGridV1Document(documentValue));
    }
  }, [
    createInitialEditorValue,
    documentFormatFromServer,
    document,
    documentValue,
    initialFormatMetadata,
  ]);

  useEffect(() => {
    if (documentFormatFromServer === 'grid-v1') return;

    setMarkdownValue(
      documentValue ??
        (isNewDocument ? createDefaultMarkdownDocumentValue() : ''),
    );
  }, [documentFormatFromServer, documentValue, isNewDocument]);

  useEffect(() => {
    if (isMarkdownDocument || isGridDocument) return;

    prevSerializedForAutoSaveRef.current = editorJsStableStringForDirtyCompare(
      parseEditorJs(lastSavedSerialized) ?? null,
    );
  }, [isGridDocument, isMarkdownDocument, lastSavedSerialized]);

  const getSerializedDocument = useCallback(() => {
    if (isGridDocument) {
      return serializeGridV1Document(gridDocumentValue);
    }

    if (isMarkdownDocument) return markdownValue;

    return stringifyEditorJs(getValue() ?? editorValue ?? null);
  }, [
    editorValue,
    getValue,
    gridDocumentValue,
    isGridDocument,
    isMarkdownDocument,
    markdownValue,
  ]);

  const applyAfterSave = useCallback(
    ({ savedValue, sentValue }: { savedValue: string; sentValue: string }) => {
      if (isMarkdownDocument) {
        setMarkdownValue(savedValue);
        setIsDirty(false);
        setLastSavedSerialized(savedValue);
        setSavedDocumentFormat('markdown-v1');
        setDocumentFormat('markdown-v1');
        return;
      }

      if (isGridDocument) {
        const parsed = parseGridV1Document(savedValue);
        setGridDocumentValue(parsed);
        setIsDirty(false);
        setLastSavedSerialized(savedValue);
        setSavedDocumentFormat('grid-v1');
        setDocumentFormat('grid-v1');
        return;
      }

      const sentData = parseEditorJs(sentValue) ?? null;
      const currentData = getValue() ?? editorValue ?? null;
      const sentStable = editorJsStableStringForDirtyCompare(sentData);
      const currentStable = editorJsStableStringForDirtyCompare(currentData);
      if (sentStable === currentStable) {
        const savedData = parseEditorJs(savedValue) ?? null;
        setValue(savedData);
        setEditorValue(savedData);
        setIsDirty(false);
      } else {
        setEditorValue(currentData);
        setIsDirty(true);
      }

      setLastSavedSerialized(savedValue);
      setSavedDocumentFormat('editorjs-v1');
      setDocumentFormat('editorjs-v1');
    },
    [editorValue, getValue, isGridDocument, isMarkdownDocument, setValue],
  );

  const convertToMarkdown = useCallback(async () => {
    if (documentFormat !== 'editorjs-v1') {
      return;
    }

    try {
      await flushSave();
      const currentEditorValue = getValue() ?? editorValue;
      const converted = convertEditorJsToMarkdown({
        data: currentEditorValue,
        displayPath,
      });
      setEditorValue(currentEditorValue);
      setMarkdownValue(converted);
      setFormatMetadata(null);
      setDocumentFormat('markdown-v1');
      setIsDirty(true);
      setDirtyEditGeneration((n) => n + 1);
      setEditorPlaceholderMuted(false);
      toaster.create({
        title: 'Converted',
        description: 'Review the markdown result, then save if it looks right.',
        type: 'success',
      });
    } catch (error) {
      console.error('Editor.js to markdown conversion failed', {
        displayPath,
        error,
        details:
          error instanceof Error && 'details' in error
            ? (error as ConvertEditorJsToMarkdownError).details
            : undefined,
      });
      toaster.create({
        title: 'Conversion failed',
        description:
          error instanceof Error
            ? error.message
            : 'Unexpected content prevented conversion.',
        type: 'error',
      });
    }
  }, [displayPath, documentFormat, editorValue, flushSave, getValue]);

  const convertToGrid = useCallback(async () => {
    if (documentFormat === 'grid-v1') {
      return;
    }

    try {
      await flushSave();

      let markdown: string;
      if (documentFormat === 'markdown-v1') {
        markdown = markdownValue;
      } else if (documentFormat === 'editorjs-v1') {
        const currentEditorValue = getValue() ?? editorValue;
        setEditorValue(currentEditorValue);
        markdown = convertEditorJsToMarkdown({
          data: currentEditorValue,
          displayPath,
        });
      } else {
        return;
      }

      setGridDocumentValue(createGridV1DocumentFromMarkdown(markdown));
      setFormatMetadata(null);
      setDocumentFormat('grid-v1');
      setIsDirty(true);
      setDirtyEditGeneration((n) => n + 1);
      setEditorPlaceholderMuted(false);
      toaster.create({
        title: 'Converted',
        description: 'Review the grid layout, then save if it looks right.',
        type: 'success',
      });
    } catch (error) {
      console.error('Convert to grid failed', {
        displayPath,
        documentFormat,
        error,
        details:
          error instanceof Error && 'details' in error
            ? (error as ConvertEditorJsToMarkdownError).details
            : undefined,
      });
      toaster.create({
        title: 'Conversion failed',
        description:
          error instanceof Error
            ? error.message
            : 'Unexpected content prevented conversion.',
        type: 'error',
      });
    }
  }, [
    displayPath,
    documentFormat,
    editorValue,
    flushSave,
    getValue,
    markdownValue,
  ]);

  const save = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (saveInFlightRef.current) return;

      saveInFlightRef.current = true;
      try {
        await flushSave();
        const value = getSerializedDocument();
        const { data } = await upsertDocument({
          variables: {
            input: {
              id: documentId ?? path,
              path: isNewDocument ? path : displayPath,
              value,
              format:
                documentFormat === 'markdown-v1'
                  ? 'markdown-v1'
                  : documentFormat === 'grid-v1'
                    ? 'grid-v1'
                    : 'editorjs-v1',
              formatMetadata: isMarkdownDocument ? formatMetadata : undefined,
            },
          },
        });

        if (!silent) {
          toaster.create({
            title: 'Success',
            description: isNewDocument
              ? 'Document created successfully'
              : 'Document saved successfully',
          });
        }

        const savedValueString = data?.upsertDocument?.document?.value ?? value;
        const nextFormatMetadata =
          data?.upsertDocument?.document?.formatMetadata ?? null;
        setFormatMetadata(nextFormatMetadata);
        setSavedFormatMetadata(nextFormatMetadata);
        const editTs = data?.upsertDocument?.documentMetadata?.editTimestamp;
        if (typeof editTs === 'number') {
          lastServerEditTimestampRef.current = editTs;
        }

        applyAfterSave({ savedValue: savedValueString, sentValue: value });
        setSavedDocumentFormat(documentFormat);

        await refetchInTransition(refetch);

        if (isNewDocument) {
          navigate(documentViewHref(path), { replace: true });
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [
      applyAfterSave,
      displayPath,
      documentFormat,
      documentId,
      formatMetadata,
      getSerializedDocument,
      isMarkdownDocument,
      isNewDocument,
      navigate,
      path,
      refetch,
      flushSave,
      lastServerEditTimestampRef,
      upsertDocument,
    ],
  );

  const revertToSaved = useCallback(() => {
    if (savedDocumentFormat === 'markdown-v1') {
      setDocumentFormat('markdown-v1');
      setFormatMetadata(savedFormatMetadata);
      setMarkdownValue(lastSavedSerialized);
      setIsDirty(false);
      return;
    }

    if (savedDocumentFormat === 'grid-v1') {
      setDocumentFormat('grid-v1');
      setFormatMetadata(savedFormatMetadata);
      setGridDocumentValue(parseGridV1Document(lastSavedSerialized));
      setIsDirty(false);
      return;
    }

    const savedData = parseEditorJs(lastSavedSerialized) ?? null;
    setDocumentFormat('editorjs-v1');
    setFormatMetadata(savedFormatMetadata);
    setEditorValue(savedData);
    if (!isMarkdownDocument) {
      setValue(savedData);
    }

    setIsDirty(false);
  }, [
    isMarkdownDocument,
    lastSavedSerialized,
    savedDocumentFormat,
    savedFormatMetadata,
    setValue,
  ]);

  useEffect(() => {
    let alive = true;

    const checkForChanges = async () => {
      if (isMarkdownDocument) {
        const dirty = markdownValue !== lastSavedSerialized;
        setIsDirty(dirty);
        if (markdownValue !== prevSerializedForAutoSaveRef.current) {
          prevSerializedForAutoSaveRef.current = markdownValue;
          if (dirty) {
            setDirtyEditGeneration((n) => n + 1);
          }
        }

        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
        return;
      }

      if (isGridDocument) {
        const currentStable =
          gridV1StableStringForDirtyCompare(gridDocumentValue);
        const dirty = currentStable !== lastSavedSerialized;
        setIsDirty(dirty);
        if (currentStable !== prevSerializedForAutoSaveRef.current) {
          prevSerializedForAutoSaveRef.current = currentStable;
          if (dirty) {
            setDirtyEditGeneration((n) => n + 1);
          }
        }

        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
        return;
      }

      try {
        await flushSave();
      } catch {
        return;
      }
      if (!alive) return;

      const editorData = getValue() ?? editorValue ?? null;
      const currentStable = editorJsStableStringForDirtyCompare(editorData);
      const savedStable = editorJsStableStringForDirtyCompare(
        parseEditorJs(lastSavedSerialized) ?? null,
      );
      const dirty = currentStable !== savedStable;
      setIsDirty(dirty);
      if (currentStable !== prevSerializedForAutoSaveRef.current) {
        prevSerializedForAutoSaveRef.current = currentStable;
        if (dirty) {
          setDirtyEditGeneration((n) => n + 1);
        }
      }

      if (isNewDocument) {
        const stillDefaultPlaceholder =
          editorData == null ||
          editorJsStableStringForNewDocPlaceholderCompare(editorData) ===
            newDocPlaceholderBaselineRef.current;
        setEditorPlaceholderMuted((prev) =>
          prev === stillDefaultPlaceholder ? prev : stillDefaultPlaceholder,
        );
      } else {
        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
      }
    };

    const interval = setInterval(() => void checkForChanges(), 500);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [
    flushSave,
    editorValue,
    getValue,
    gridDocumentValue,
    isGridDocument,
    isMarkdownDocument,
    isNewDocument,
    lastSavedSerialized,
    markdownValue,
  ]);

  const value = useMemo<DocumentContextValue>(
    () => ({
      document,
      path,
      displayPath,
      documentId,
      documentFormat,
      formatMetadata,
      canConvertToMarkdown: documentFormat === 'editorjs-v1',
      canConvertToGrid:
        documentFormat === 'editorjs-v1' || documentFormat === 'markdown-v1',
      isMarkdownDocument,
      isGridDocument,
      isNewDocument,
      editorPlaceholderMuted,
      childrenItems,
      childrenHasMore,
      editorValue,
      markdownValue,
      setMarkdownValue,
      gridDocumentValue,
      setGridDocumentValue,
      lastSavedSerialized,
      isDirty,
      dirtyEditGeneration,
      getSerializedDocument,
      convertToMarkdown,
      convertToGrid,
      save,
      saving,
      saveInFlightRef,
      revertToSaved,
      refetch,
    }),
    [
      childrenHasMore,
      childrenItems,
      convertToGrid,
      convertToMarkdown,
      documentFormat,
      dirtyEditGeneration,
      displayPath,
      document,
      documentId,
      editorValue,
      editorPlaceholderMuted,
      formatMetadata,
      getSerializedDocument,
      gridDocumentValue,
      isDirty,
      isGridDocument,
      isMarkdownDocument,
      isNewDocument,
      lastSavedSerialized,
      markdownValue,
      path,
      refetch,
      revertToSaved,
      save,
      saving,
    ],
  );

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
}

export function useDocumentContext(): DocumentContextValue {
  const ctx = useContext(DocumentContext);
  if (!ctx) {
    throw new Error('useDocumentContext must be used within DocumentProvider');
  }

  return ctx;
}
